import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  addDays,
  differenceInCalendarDays,
  differenceInMinutes,
  endOfDay,
  format,
  isValid,
  startOfDay,
} from "date-fns";
import { faChevronDown, faChevronUp, faPlus } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip from "../../components/Tooltip";
import { BOOKING_APPEARANCE, getAppearanceText, getBookingAppearance, getBookingKind, isCanceledOrNoShowBooking, isTimelineBookingWithoutBuffer } from "./BookingLegend";
import { getStatusLabel } from "../../helpers/StatusMapper";

const SLOT_MINUTES_QUARTER = 15;
const SLOT_MINUTES_DAY = 24 * 60;
const SLOT_WIDTH_MAX = 48;
const TARGET_TIMELINE_WIDTH = 1400;
const CONNECTOR_COL_WIDTH_DESKTOP = 288;
const CONNECTOR_COL_WIDTH_MOBILE = 188;
const TIMELINE_ROW_MIN_HEIGHT = 80;
const TIMELINE_ROW_PADDING = 6;
const TIMELINE_CARD_GAP = 6;
const TIMELINE_CARD_HEIGHT = 52;
const TIMELINE_SEGMENT_GAP = 3;
const STRIP_ICON_ONLY_MAX = 40;
const STRIP_SHORT_LABEL_MAX = 100;

const getStripDensity = (widthPx) => {
  const width = Number(widthPx) || 0;

  if (width < STRIP_ICON_ONLY_MAX) {
    return "icon";
  }

  if (width < STRIP_SHORT_LABEL_MAX) {
    return "short";
  }

  return "full";
};

/** Hub buffer minutes from API (BookingHubBuffers + terms per connector). */
const getConnectorHubBufferMinutes = (connector) => {
  const start = connector?.hubStartBufferMinutes;
  const late = connector?.hubLateStopBufferMinutes;
  return {
    start: Number.isFinite(start) ? Math.max(0, start) : 0,
    late: Number.isFinite(late) ? Math.max(0, late) : 0,
  };
};

const toDate = (value) => {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  return isValid(date) ? date : null;
};

const normalizeNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getSlotWidth = (slotCount, slotMinutes) => {
  if (slotMinutes < SLOT_MINUTES_DAY) {
    return SLOT_WIDTH_MAX;
  }

  const safeSlotCount = Math.max(slotCount, 1);
  const preferredWidth = Math.floor(TARGET_TIMELINE_WIDTH / safeSlotCount);

  return Math.max(Math.min(preferredWidth, 144), 96);
};

const getTimelineScale = (totalMinutes) => {
  if (totalMinutes > 3 * SLOT_MINUTES_DAY) {
    return {
      slotMinutes: SLOT_MINUTES_DAY,
      alignStart: startOfDay,
      alignEnd: endOfDay,
      labelFormat: "dd MMM",
    };
  }

  return {
    slotMinutes: SLOT_MINUTES_QUARTER,
    alignStart: roundDownToQuarter,
    alignEnd: roundUpToQuarter,
    labelFormat: "HH:mm",
  };
};

// Day columns follow calendar dates. A DST day is 23 or 25 hours, so stepping by 24h shifts every later column by a day.
const buildTimelineSlots = (alignedViewStart, alignedViewEnd, slotMinutes, totalMinutes) => {
  if (slotMinutes >= SLOT_MINUTES_DAY) {
    const dayCount = Math.max(
      differenceInCalendarDays(startOfDay(alignedViewEnd), startOfDay(alignedViewStart)) + 1,
      1
    );

    return Array.from({ length: dayCount }, (_, index) => addDays(startOfDay(alignedViewStart), index));
  }

  const slotCount = Math.max(Math.ceil(totalMinutes / slotMinutes), 1);
  return Array.from({ length: slotCount }, (_, index) =>
    new Date(alignedViewStart.getTime() + index * slotMinutes * 60000)
  );
};

const roundToNearestHalfHour = (date) => {
  const next = new Date(date);
  const fractionalMinutes = next.getMinutes() + next.getSeconds() / 60 + next.getMilliseconds() / 60000;
  const snappedMinutes = Math.round(fractionalMinutes / 30) * 30;
  next.setSeconds(0, 0);

  if (snappedMinutes >= 60) {
    next.setHours(next.getHours() + 1, 0, 0, 0);
    return next;
  }

  next.setMinutes(snappedMinutes, 0, 0);
  return next;
};

const roundDownToQuarter = (date) => {
  if (!date) {
    return date;
  }

  const next = new Date(date);
  const minutes = next.getMinutes();
  const roundedMinutes = Math.floor(minutes / 15) * 15;
  next.setMinutes(roundedMinutes, 0, 0);
  return next;
};

const roundUpToQuarter = (date) => {
  if (!date) {
    return date;
  }

  const next = new Date(date);
  const minutes = next.getMinutes();
  const remainder = minutes % 15;

  if (remainder !== 0 || next.getSeconds() !== 0 || next.getMilliseconds() !== 0) {
    next.setMinutes(minutes + (15 - remainder), 0, 0);
  } else {
    next.setSeconds(0, 0);
  }

  return next;
};

const getMarkerIcon = (booking) => getBookingAppearance(booking).icon;

// Admin overview leaves isOwn unset (null) → treat as visible.
// Customer overview always sets a boolean; missing/false is Occupied.
const canSeeBookingCustomer = (booking) => booking?.isOwn !== false;

const countTimelineBookings = (bookings) =>
  (Array.isArray(bookings) ? bookings : []).filter(
    (booking) => !isCanceledOrNoShowBooking(booking)
  ).length;

const getBookingLabel = (booking, t) => {
  if (!canSeeBookingCustomer(booking)) {
    return t("booking.occupied");
  }

  if (isTimelineBookingWithoutBuffer(booking)) {
    const appearance = getBookingAppearance(booking);
    const appearanceLabel = getAppearanceText(appearance, t, "label");
    return booking?.customerName
      ? `${appearanceLabel}: ${booking.customerName}`
      : appearanceLabel;
  }

  return (
    booking?.customerName ||
    booking?.bookingType ||
    booking?.name ||
    booking?.title ||
    booking?.status ||
    t("entity.booking")
  );
};

const getStripLabel = (appearance, density, t) => {
  if (density === "icon") {
    return null;
  }

  if (density === "short") {
    return getAppearanceText(appearance, t, "shortDescription")
      || getAppearanceText(appearance, t, "shortLabel")
      || getAppearanceText(appearance, t, "description")
      || getAppearanceText(appearance, t, "label");
  }

  return getAppearanceText(appearance, t, "description") || getAppearanceText(appearance, t, "label");
};

const TimelineStripContent = ({
  appearance,
  widthPx,
  timeLabel = null,
  showIcon = true,
  variant = "buffer",
}) => {
  const { t } = useTranslation();
  const density = getStripDensity(widthPx);
  const label = getStripLabel(appearance, density, t);
  const showTime =
    Boolean(timeLabel) &&
    (density === "full" || (density === "short" && widthPx >= 70));

  if (density === "icon") {
    if (!showIcon) {
      return null;
    }

    return (
      <div className="absolute inset-0 flex items-center justify-center">
        <span
          className={`inline-flex h-6 w-6 items-center justify-center rounded-full ${appearance.iconWrapClassName || ""}`}
        >
          <FontAwesomeIcon icon={appearance.icon} className="text-[11px]" />
        </span>
      </div>
    );
  }

  return (
    <div
      className={`absolute inset-0 flex min-w-0 ${
        variant === "buffer" ? "pl-2 pr-1.5" : "pl-2.5 pr-2"
      } py-1.5 ${showTime ? "flex-col justify-between" : "items-center"}`}
    >
      <div className="flex min-w-0 items-center gap-1.5">
        {showIcon && (
          <span
            className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${appearance.iconWrapClassName || ""}`}
          >
            <FontAwesomeIcon icon={appearance.icon} className="text-[10px]" />
          </span>
        )}
        {label && (
          <span className="min-w-0 truncate text-[11px] font-semibold tracking-tight leading-tight">
            {label}
          </span>
        )}
      </div>

      {showTime && (
        <div className="min-w-0 w-full truncate text-[10px] font-medium tabular-nums tracking-tight opacity-70">
          {timeLabel}
        </div>
      )}
    </div>
  );
};

const getBookingTimeLabel = (booking, t) => {
  const start = toDate(booking?.startDateTimeUtc ?? booking?.startDateTime ?? booking?.start);
  const end = toDate(booking?.endDateTimeUtc ?? booking?.endDateTime ?? booking?.end);

  if (!start || !end) {
    return t("booking.invalidTime");
  }

  return `${format(start, "HH:mm")}–${format(end, "HH:mm")}`;
};

const getTooltipDateTime = (value) => {
  const date = toDate(value);

  if (!date) {
    return null;
  }

  return format(date, "dd MMM yyyy HH:mm");
};

const getBookingBufferedStart = (booking, start, hubStartBufferMinutes) => {
  if (isTimelineBookingWithoutBuffer(booking)) {
    return start;
  }

  const explicitBufferedStart = toDate(
    booking?.bufferStartDateTimeUtc ??
      booking?.bufferStartDateTime ??
      booking?.bufferStart ??
      booking?.bufferBeginDateTimeUtc
  );

  if (explicitBufferedStart) {
    return explicitBufferedStart;
  }

  const preBufferMinutes = normalizeNumber(
    booking?.bufferBeforeMinutes ?? booking?.bufferMinutesBefore ?? booking?.preBufferMinutes
  );

  if (preBufferMinutes > 0) {
    return new Date(start.getTime() - preBufferMinutes * 60000);
  }

  if (getBookingKind(booking).includes("UNAVAILABILITY")) {
    return start;
  }

  return new Date(start.getTime() - hubStartBufferMinutes * 60000);
};

const getInferredBufferAfterMinutes = (booking, start, end, hubLateStopBufferMinutes) => {
  if (isTimelineBookingWithoutBuffer(booking)) {
    return 0;
  }

  const explicitBufferedEnd = toDate(
    booking?.bufferEndDateTimeUtc ??
      booking?.bufferEndDateTime ??
      booking?.bufferEnd ??
      booking?.bufferFinishDateTimeUtc
  );

  if (explicitBufferedEnd) {
    return Math.max(0, differenceInMinutes(explicitBufferedEnd, end));
  }

  const postBufferMinutes = normalizeNumber(
    booking?.bufferAfterMinutes ?? booking?.bufferMinutesAfter ?? booking?.postBufferMinutes
  );

  if (postBufferMinutes > 0) {
    return postBufferMinutes;
  }

  if (getBookingKind(booking).includes("UNAVAILABILITY")) {
    return 0;
  }

  const explicitBufferedStart = toDate(
    booking?.bufferStartDateTimeUtc ??
      booking?.bufferStartDateTime ??
      booking?.bufferStart ??
      booking?.bufferBeginDateTimeUtc
  );

  if (explicitBufferedStart) {
    const preMinutes = Math.max(0, differenceInMinutes(start, explicitBufferedStart));
    return Math.max(hubLateStopBufferMinutes, preMinutes);
  }

  return hubLateStopBufferMinutes;
};

const getBookingBufferedEnd = (booking, start, end, hubLateStopBufferMinutes) => {
  const afterMinutes = getInferredBufferAfterMinutes(booking, start, end, hubLateStopBufferMinutes);
  return new Date(end.getTime() + afterMinutes * 60000);
};

const getDaySlotRange = (rangeStart, rangeEnd, viewStartDate) => {
  const safeRangeEnd = new Date(rangeEnd.getTime() - 1);
  const startSlot = Math.max(
    differenceInCalendarDays(startOfDay(rangeStart), startOfDay(viewStartDate)),
    0
  );
  const endSlot = Math.max(
    differenceInCalendarDays(startOfDay(safeRangeEnd), startOfDay(viewStartDate)) + 1,
    startSlot + 1
  );

  return {
    startSlot,
    endSlot,
  };
};

const getIntervalSlotRange = (intervalStart, intervalEnd, viewStartDate, viewEndDate, slotMinutes) => {
  if (!intervalStart || !intervalEnd) {
    return null;
  }

  if (intervalStart >= viewEndDate || intervalEnd <= viewStartDate) {
    return null;
  }

  if (slotMinutes >= SLOT_MINUTES_DAY) {
    return getDaySlotRange(intervalStart, intervalEnd, viewStartDate);
  }

  const startOffsetMinutes = Math.max(differenceInMinutes(intervalStart, viewStartDate), 0);
  const endOffsetMinutes = Math.max(differenceInMinutes(intervalEnd, viewStartDate), slotMinutes);
  const startSlot = Math.max(Math.floor(startOffsetMinutes / slotMinutes), 0);
  const endSlot = Math.max(Math.ceil(endOffsetMinutes / slotMinutes), startSlot + 1);

  return {
    startSlot,
    endSlot,
  };
};

const getBookingIntervalStart = (booking) =>
  toDate(booking?.startDateTimeUtc ?? booking?.startDateTime ?? booking?.start);

/** End of the amber "possible next start" window: hub nominal margin, clipped where the next booking's buffers begin. */
const getModelNextMarginEnd = (bufferedEnd, hubStartBufferMinutes, followingBooking) => {
  if (!bufferedEnd || hubStartBufferMinutes <= 0) {
    return null;
  }

  const marginAfterBlockedEnd = new Date(bufferedEnd.getTime() + hubStartBufferMinutes * 60000);
  const followingStart = getBookingIntervalStart(followingBooking);

  if (!followingBooking || !followingStart) {
    return marginAfterBlockedEnd;
  }

  const followingBufferedStart = getBookingBufferedStart(followingBooking, followingStart, hubStartBufferMinutes);
  return new Date(Math.min(marginAfterBlockedEnd.getTime(), followingBufferedStart.getTime()));
};

// Lane stacking uses full blocked span (buffers) for bookings so adjacent buffer zones don't overlap visually on the same row.
// Extend the lane through the "possible next start" strip whenever that strip has positive width (including a gap before a later booking).
const getLaneSlotRange = (
  booking,
  viewStartDate,
  viewEndDate,
  slotMinutes,
  hubStartBufferMinutes,
  hubLateStopBufferMinutes,
  followingBooking
) => {
  const start = toDate(booking?.startDateTimeUtc ?? booking?.startDateTime ?? booking?.start);
  const end = toDate(booking?.endDateTimeUtc ?? booking?.endDateTime ?? booking?.end);

  if (!start || !end) {
    return null;
  }

  const kind = getBookingKind(booking);
  const isUnavailability = kind.includes("UNAVAILABILITY");
  const omitBuffers = isUnavailability || isTimelineBookingWithoutBuffer(booking);
  const laneStart = omitBuffers ? start : getBookingBufferedStart(booking, start, hubStartBufferMinutes);
  let laneEnd = omitBuffers ? end : getBookingBufferedEnd(booking, start, end, hubLateStopBufferMinutes);

  if (!omitBuffers && hubStartBufferMinutes > 0) {
    const modelNextMarginEnd = getModelNextMarginEnd(laneEnd, hubStartBufferMinutes, followingBooking);
    if (modelNextMarginEnd && modelNextMarginEnd.getTime() > laneEnd.getTime()) {
      laneEnd = modelNextMarginEnd;
    }
  }

  return getIntervalSlotRange(laneStart, laneEnd, viewStartDate, viewEndDate, slotMinutes);
};

const getBookingLanes = (bookings, viewStartDate, viewEndDate, slotMinutes, hubStartBufferMinutes, hubLateStopBufferMinutes) => {
  const sortedBookings = [...bookings].sort((a, b) => {
    const first = toDate(a?.startDateTimeUtc ?? a?.startDateTime ?? a?.start);
    const second = toDate(b?.startDateTimeUtc ?? b?.startDateTime ?? b?.start);

    return (first?.getTime() ?? 0) - (second?.getTime() ?? 0);
  });

  const laneEndSlots = [];

  return sortedBookings.map((booking, sortedIndex) => {
    const followingBooking = sortedBookings.slice(sortedIndex + 1).find(
      (candidate) => !isTimelineBookingWithoutBuffer(candidate)
    ) ?? null;
    const slotRange = getLaneSlotRange(
      booking,
      viewStartDate,
      viewEndDate,
      slotMinutes,
      hubStartBufferMinutes,
      hubLateStopBufferMinutes,
      followingBooking
    );

    if (!slotRange) {
      return {
        booking,
        laneIndex: 0,
        slotRange: null,
        followingBooking,
      };
    }

    let laneIndex = laneEndSlots.findIndex((laneEndSlot) => slotRange.startSlot >= laneEndSlot);

    if (laneIndex === -1) {
      laneIndex = laneEndSlots.length;
      laneEndSlots.push(slotRange.endSlot);
    } else {
      laneEndSlots[laneIndex] = slotRange.endSlot;
    }

    return {
      booking,
      laneIndex,
      slotRange,
      followingBooking,
    };
  });
};

const BookingCard = ({
  booking,
  viewStartDate,
  viewEndDate,
  slotWidth,
  slotMinutes,
  laneIndex,
  laneHeight,
  rowPadding,
  _compact,
  connector,
  onClick,
  followingBooking,
}) => {
  const { t } = useTranslation();
  const start = toDate(booking?.startDateTimeUtc ?? booking?.startDateTime ?? booking?.start);
  const end = toDate(booking?.endDateTimeUtc ?? booking?.endDateTime ?? booking?.end);

  if (!start || !end) {
    return null;
  }

  const { start: hubStartBufferMinutes, late: hubLateStopBufferMinutes } = getConnectorHubBufferMinutes(connector);
  const bufferedStart = getBookingBufferedStart(booking, start, hubStartBufferMinutes);
  const bufferedEnd = getBookingBufferedEnd(booking, start, end, hubLateStopBufferMinutes);
  const preBufferMinutes = Math.max(differenceInMinutes(start, bufferedStart), 0);
  const postBufferMinutes = Math.max(differenceInMinutes(bufferedEnd, end), 0);
  const overlapsView = start < viewEndDate && end > viewStartDate;

  if (!overlapsView) {
    return null;
  }

  const startOffsetMinutes = Math.max(differenceInMinutes(start, viewStartDate), 0);
  const endOffsetMinutes = Math.max(differenceInMinutes(end, viewStartDate), slotMinutes);
  const visibleBufferedStart = bufferedStart > viewStartDate ? bufferedStart : viewStartDate;
  const visibleBufferedEnd = bufferedEnd < viewEndDate ? bufferedEnd : viewEndDate;
  const wrapperStartOffsetMinutes = Math.max(differenceInMinutes(visibleBufferedStart, viewStartDate), 0);
  const wrapperEndOffsetMinutes = Math.max(differenceInMinutes(visibleBufferedEnd, viewStartDate), slotMinutes);
  const wrapperWidthMinutes = Math.max(differenceInMinutes(visibleBufferedEnd, visibleBufferedStart), slotMinutes);
  const bookingWidthMinutes = Math.max(differenceInMinutes(end, start), slotMinutes);
  const isDayView = slotMinutes >= SLOT_MINUTES_DAY;
  const wrapperDayRange = isDayView
    ? getDaySlotRange(visibleBufferedStart, visibleBufferedEnd, viewStartDate)
    : null;
  const bookingDayRange = isDayView ? getDaySlotRange(start, end, viewStartDate) : null;
  const wrapperStartSlot = isDayView
    ? wrapperDayRange.startSlot
    : Math.max(Math.floor(wrapperStartOffsetMinutes / slotMinutes), 0);
  const wrapperEndSlot = isDayView
    ? wrapperDayRange.endSlot
    : Math.max(Math.ceil(wrapperEndOffsetMinutes / slotMinutes), wrapperStartSlot + 1);
  const bookingStartSlot = isDayView
    ? bookingDayRange.startSlot
    : Math.max(Math.floor(startOffsetMinutes / slotMinutes), 0);
  const bookingEndSlot = isDayView
    ? bookingDayRange.endSlot
    : Math.max(Math.ceil(endOffsetMinutes / slotMinutes), bookingStartSlot + 1);
  const wrapperLeft = isDayView
    ? wrapperStartSlot * slotWidth
    : (wrapperStartOffsetMinutes / slotMinutes) * slotWidth;
  const wrapperWidth = isDayView
    ? Math.max((wrapperEndSlot - wrapperStartSlot) * slotWidth, slotWidth)
    : Math.max((wrapperWidthMinutes / slotMinutes) * slotWidth, slotWidth);
  const cardLeft = isDayView
    ? Math.max((bookingStartSlot - wrapperStartSlot) * slotWidth, 0)
    : Math.max((differenceInMinutes(start, visibleBufferedStart) / slotMinutes) * slotWidth, 0);
  const bookingWidth = isDayView
    ? Math.max((bookingEndSlot - bookingStartSlot) * slotWidth, slotWidth)
    : Math.max((bookingWidthMinutes / slotMinutes) * slotWidth, slotWidth);
  // Width from time deltas — avoids residual math (wrapper − card − nominal) rounding away the post-buffer strip.
  const preBufferWidth = isDayView
    ? Math.max((bookingStartSlot - wrapperStartSlot) * slotWidth, 0)
    : Math.max((differenceInMinutes(start, visibleBufferedStart) / slotMinutes) * slotWidth, 0);
  const postBufferWidth = isDayView
    ? Math.max((wrapperEndSlot - bookingEndSlot) * slotWidth, 0)
    : Math.max((differenceInMinutes(visibleBufferedEnd, end) / slotMinutes) * slotWidth, 0);

  const kind = getBookingKind(booking);
  const isUnavailability = kind.includes("UNAVAILABILITY");
  const omitBuffers = isUnavailability || isTimelineBookingWithoutBuffer(booking);

  const modelNextMarginEnd = omitBuffers
    ? null
    : getModelNextMarginEnd(bufferedEnd, hubStartBufferMinutes, followingBooking);
  const visibleNextMarginEnd =
    modelNextMarginEnd && modelNextMarginEnd < viewEndDate ? modelNextMarginEnd : viewEndDate;
  const nextMarginMinutes =
    hubStartBufferMinutes > 0 &&
    modelNextMarginEnd &&
    visibleNextMarginEnd > visibleBufferedEnd
      ? Math.max(0, differenceInMinutes(visibleNextMarginEnd, visibleBufferedEnd))
      : 0;
  const nextMarginWidth =
    nextMarginMinutes > 0
      ? Math.max((nextMarginMinutes / slotMinutes) * slotWidth, 0)
      : 0;
  const outerWidth = wrapperWidth + nextMarginWidth;

  const bufferBeforeAppearance = BOOKING_APPEARANCE.BUFFER_BEFORE;
  const bufferAfterAppearance = BOOKING_APPEARANCE.BUFFER_AFTER;
  const bufferNextStartAppearance = BOOKING_APPEARANCE.BUFFER_NEXT_START;
    const isAnonymousOccupied = !canSeeBookingCustomer(booking);
    const isInactiveBooking = isTimelineBookingWithoutBuffer(booking);
    const bookingId = isAnonymousOccupied ? "-" : booking?.bookingPublicId || "-";
  const customerLabel = isAnonymousOccupied
    ? t("booking.occupied")
    : booking?.customerName || booking?.customerPublicId || "-";
  const typeLabel = isAnonymousOccupied
    ? t("booking.occupied")
    : kind.includes("UNAVAILABILITY")
      ? t("booking.unavailability")
      : t("entity.booking");
  const statusKey = String(booking?.reservationStatus || "").toUpperCase();
  const tooltipContent = (
    <div className="flex min-w-[14rem] flex-col gap-2 text-left">
      <div className="border-b border-mute-200 pb-1.5">
        <div className="text-sm font-bold text-primary-500">{getBookingLabel(booking, t)}</div>
        {!isAnonymousOccupied && bookingId !== "-" && (
          <div className="mt-0.5 text-[11px] text-mute-500">ID: {bookingId}</div>
        )}
      </div>

      <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[11px]">
        <span className="font-semibold text-mute-600">{t("common.type")}</span>
        <span>{typeLabel}</span>

        <span className="font-semibold text-mute-600">{t("common.start")}</span>
        <span className="tabular-nums">{getTooltipDateTime(start) || "-"}</span>

        <span className="font-semibold text-mute-600">{t("common.end")}</span>
        <span className="tabular-nums">{getTooltipDateTime(end) || "-"}</span>

        {!isUnavailability && !isAnonymousOccupied && (
          <>
            <span className="font-semibold text-mute-600">{t("entity.customer")}</span>
            <span>{customerLabel}</span>
          </>
        )}

        {!isAnonymousOccupied && booking?.reservationStatus && (
          <>
            <span className="font-semibold text-mute-600">{t("common.status")}</span>
            <span>{t(`booking.status.${statusKey}`, { defaultValue: String(booking.reservationStatus).replace(/_/g, " ") })}</span>
          </>
        )}

        {!isAnonymousOccupied && booking?.bookingSource && (
          <>
            <span className="font-semibold text-mute-600">{t("booking.source")}</span>
            <span>{booking.bookingSource}</span>
          </>
        )}
      </div>

      {!omitBuffers && (preBufferMinutes > 0 || postBufferMinutes > 0 || nextMarginMinutes > 0) && (
        <div className="border-t border-mute-200 pt-1.5 text-[11px] text-mute-600">
          {!isUnavailability && (preBufferMinutes > 0 || postBufferMinutes > 0) && (
            <div>
              <span className="font-semibold text-primary-500">{t("booking.blocked")}:</span>{" "}
              <span className="tabular-nums">
                {getTooltipDateTime(bufferedStart) || "-"} – {getTooltipDateTime(bufferedEnd) || "-"}
              </span>
            </div>
          )}
          {preBufferMinutes > 0 && (
            <div>{t("booking.earlyStartBuffer", { minutes: preBufferMinutes })}</div>
          )}
          {postBufferMinutes > 0 && (
            <div>{t("booking.overstayBuffer", { minutes: postBufferMinutes })}</div>
          )}
          {nextMarginMinutes > 0 && (
            <div>{t("booking.possibleNextStart", { minutes: nextMarginMinutes })}</div>
          )}
        </div>
      )}
    </div>
  );

    const preVisualWidth =
      preBufferWidth > 0 && bookingWidth > 0
        ? Math.max(preBufferWidth - TIMELINE_SEGMENT_GAP, 0)
        : preBufferWidth;
    const bookVisualWidth =
      bookingWidth > 0 && (postBufferWidth > 0 || nextMarginWidth > 0)
        ? Math.max(bookingWidth - TIMELINE_SEGMENT_GAP, 0)
        : bookingWidth;
    const postVisualWidth =
      postBufferWidth > 0 && nextMarginWidth > 0
        ? Math.max(postBufferWidth - TIMELINE_SEGMENT_GAP, 0)
        : postBufferWidth;

    const bookingAppearance = getBookingAppearance(booking);
    const bookingDensity = getStripDensity(bookVisualWidth);
    const bookingDisplayLabel =
      bookingDensity === "icon"
        ? null
        : getBookingLabel(booking, t);
    const showBookingTime =
      bookingDensity === "full" ||
      (bookingDensity === "short" && bookVisualWidth >= 70);
    const showBookingIcon =
      bookingDensity === "icon" ||
      (bookingDensity === "full" && bookVisualWidth >= 140);
    const showBookingLabel = Boolean(bookingDisplayLabel);
    const nextTimeLabel =
      nextMarginMinutes > 0
        ? `${format(bufferedEnd, "HH:mm")}–${format(
            modelNextMarginEnd && modelNextMarginEnd < viewEndDate
              ? modelNextMarginEnd
              : viewEndDate,
            "HH:mm"
          )}`
        : null;

    return (
        <div
            className="absolute min-w-0"
            style={{
                left: `${wrapperLeft}px`,
                top: `${rowPadding + laneIndex * (laneHeight + TIMELINE_CARD_GAP)}px`,
                width: `${outerWidth}px`,
                height: `${laneHeight}px`,
            }}
        >
            {!omitBuffers && preBufferWidth > 0 && (
                <div
                    className={`pointer-events-none absolute inset-y-0.5 left-0 z-30 overflow-hidden rounded-lg border ${bufferBeforeAppearance.className}`}
                    style={{
                      width: `${preVisualWidth}px`,
                      backgroundImage:
                        "repeating-linear-gradient(-45deg, transparent, transparent 5px, rgba(0,47,108,0.06) 5px, rgba(0,47,108,0.06) 10px)",
                    }}
                    title={t("booking.bufferBeforeTitle", {
                      description: getAppearanceText(bufferBeforeAppearance, t, "description"),
                      minutes: preBufferMinutes,
                    })}
                >
                    <span className={`absolute inset-y-0 left-0 w-0.5 ${bufferBeforeAppearance.accentClassName}`} />
                    <TimelineStripContent
                      appearance={bufferBeforeAppearance}
                      widthPx={preVisualWidth}
                      timeLabel={
                        preBufferMinutes > 0
                          ? `${format(bufferedStart, "HH:mm")}–${format(start, "HH:mm")}`
                          : null
                      }
                    />
                </div>
            )}

            <Tooltip
                content={tooltipContent}
                position="bottom-left"
                className="absolute inset-y-0 z-10"
                layoutClassName="block h-full"
                tooltipClassName="max-w-xs"
                style={{
                    left: `${cardLeft}px`,
                    width: `${bookVisualWidth}px`,
                }}
            >
                <button
                    type="button"
                    className={`absolute inset-y-0.5 left-0 right-0 overflow-hidden rounded-lg border text-left shadow-sm transition ${
                      showBookingTime ? "flex flex-col justify-between py-1.5 pl-2 pr-1.5" : "flex items-center py-1.5 pl-2 pr-1.5"
                    } ${bookingAppearance.className} ${isAnonymousOccupied || isInactiveBooking ? "cursor-default" : "cursor-pointer"}`}
                    onClick={() => {
                      if (isAnonymousOccupied || isInactiveBooking) {
                        return;
                      }

                      onClick?.(booking, connector);
                    }}
                >
                    <span className={`absolute inset-y-0 left-0 w-1 ${bookingAppearance.accentClassName}`} />
                    <div className="flex min-w-0 w-full items-center gap-1">
                        {showBookingIcon && (
                          <span
                            className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${bookingAppearance.iconWrapClassName} ${
                              bookingDensity === "icon" ? "mx-auto" : ""
                            }`}
                          >
                            <FontAwesomeIcon
                              icon={getMarkerIcon(booking)}
                              className="text-[10px]"
                            />
                          </span>
                        )}
                        {showBookingLabel && (
                          <span className="min-w-0 flex-1 truncate text-xs font-semibold tracking-tight leading-none">
                            {bookingDisplayLabel}
                          </span>
                        )}
                    </div>

                    {showBookingTime && (
                      <div className="min-w-0 w-full truncate text-[10px] font-medium tabular-nums tracking-tight opacity-70">
                        {getBookingTimeLabel(booking, t)}
                      </div>
                    )}
                </button>
            </Tooltip>

            {!omitBuffers && postBufferWidth > 0 && (
                <div
                    className={`pointer-events-none absolute inset-y-0.5 z-30 overflow-hidden rounded-lg border ${bufferAfterAppearance.className}`}
                    style={{
                      left: `${cardLeft + bookingWidth}px`,
                      width: `${postVisualWidth}px`,
                      backgroundImage:
                        "repeating-linear-gradient(-45deg, transparent, transparent 5px, rgba(0,47,108,0.06) 5px, rgba(0,47,108,0.06) 10px)",
                    }}
                    title={t("booking.bufferAfterTitle", {
                      description: getAppearanceText(bufferAfterAppearance, t, "description"),
                      minutes: postBufferMinutes,
                    })}
                >
                    <span className={`absolute inset-y-0 left-0 w-0.5 ${bufferAfterAppearance.accentClassName}`} />
                    <TimelineStripContent
                      appearance={bufferAfterAppearance}
                      widthPx={postVisualWidth}
                      timeLabel={
                        postBufferMinutes > 0
                          ? `${format(end, "HH:mm")}–${format(bufferedEnd, "HH:mm")}`
                          : null
                      }
                    />
                </div>
            )}

            {nextMarginWidth > 0 && !isDayView && !omitBuffers && (
                <div
                    className={`pointer-events-none absolute inset-y-0.5 z-30 overflow-hidden rounded-lg border ${bufferNextStartAppearance.className}`}
                    style={{ left: `${wrapperWidth}px`, width: `${nextMarginWidth}px` }}
                    title={t("booking.nextStartTitle", { minutes: nextMarginMinutes })}
                >
                    <span className={`absolute inset-y-0 left-0 w-0.5 ${bufferNextStartAppearance.accentClassName}`} />
                    <TimelineStripContent
                      appearance={bufferNextStartAppearance}
                      widthPx={nextMarginWidth}
                      timeLabel={nextTimeLabel}
                    />
                </div>
            )}
        </div>
    );
};

const ConnectorRow = ({
  connector,
  viewStartDate,
  viewEndDate,
  slots,
  connectorColWidth,
  slotWidth,
  slotMinutes,
  onBookingClick,
  onTermsClick,
  termsLabel,
  termsDisabled,
}) => {
  const { t } = useTranslation();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
  const bookings = Array.isArray(connector?.bookings) ? connector.bookings : [];
  const countedBookingCount = countTimelineBookings(bookings);
  const connectorStatus = connector?.chargingConnectorStatus || "Available";
  const normalizedStatus = String(connectorStatus).toLowerCase();
  const showStatus =
    normalizedStatus.includes("inoperative") ||
    normalizedStatus.includes("faulted") ||
    normalizedStatus.includes("unavailable");
  const { start: hubStartBufferMinutes, late: hubLateStopBufferMinutes } = getConnectorHubBufferMinutes(connector);
  const bookingLanes = useMemo(
    () =>
      getBookingLanes(
        bookings,
        viewStartDate,
        viewEndDate,
        slotMinutes,
        hubStartBufferMinutes,
        hubLateStopBufferMinutes
      ),
    [bookings, hubLateStopBufferMinutes, hubStartBufferMinutes, slotMinutes, viewEndDate, viewStartDate]
  );
  const laneCount = Math.max(
    bookingLanes.reduce((maxLane, bookingLane) => Math.max(maxLane, bookingLane.laneIndex), -1) + 1,
    1
  );
  const expandedRowHeight =
    TIMELINE_ROW_PADDING * 2 +
    laneCount * TIMELINE_CARD_HEIGHT +
    (laneCount - 1) * TIMELINE_CARD_GAP;
  const rowHeight = Math.max(
    TIMELINE_ROW_MIN_HEIGHT,
    expandedRowHeight
  );
  const laneHeight =
    laneCount > 1
      ? TIMELINE_CARD_HEIGHT
      : rowHeight - TIMELINE_ROW_PADDING * 2;

  return (
    <div className="grid border-t border-mute-200" style={{ gridTemplateColumns: `${connectorColWidth}px auto` }}>
      <div className="sticky left-0 z-20 bg-white px-3 py-2 sm:px-4 sm:py-3 border-r border-mute-200">
        <div className="flex flex-col gap-1.5 md:flex-row md:items-start md:justify-between md:gap-3">
          <div>
            <p className="text-xs sm:text-sm font-semibold leading-snug">
              {connector?.connectorName}
            </p>
            <p className="text-xs text-mute-500 mt-0.5 break-all">
              {connector?.ocpiEvseId || connector?.connectorPublicId || connector?.chargingConnectorPublicId || "-"}
            </p>
            {onTermsClick && termsDisabled !== true && (
              <div className="mt-2">
                <button
                  type="button"
                  className="btn-xs btn-outline-primary"
                  onClick={() => onTermsClick?.(connector)}
                  title={t("booking.viewEditTerms")}
                >
                  {termsLabel || t("booking.terms")}
                </button>
              </div>
            )}
          </div>
          <div className="flex flex-col items-start gap-1 text-xs text-mute-500 shrink-0 md:items-end">
            <span className="rounded-full bg-mute-100 px-2 py-0.5 font-medium text-mute-600 leading-none">
              {t(countedBookingCount === 1 ? "booking.bookingCount" : "booking.bookingsCountPlural", { count: countedBookingCount })}
            </span>
            {showStatus && (
              <span className="rounded-full bg-rose-100 px-2 py-0.5 font-medium text-rose-700 leading-none">
                {getStatusLabel(connectorStatus, t)}
              </span>
            )}
          </div>
        </div>
      </div>

      <div
        className="relative z-0 h-full self-stretch"
        style={{ width: `${slots.length * slotWidth}px`, minHeight: `${rowHeight}px` }}
      >
        <div className="grid h-full" style={{ gridTemplateColumns: `repeat(${slots.length}, ${slotWidth}px)` }}>
          {slots.map((slot, index) => (
            <div
              key={`${slot.toISOString()}-${index}`}
              className={`border-r border-mute-200 ${index % 2 === 0 ? "bg-white" : "bg-mute-50"}`}
            />
          ))}
        </div>

        <div className="absolute inset-0 z-0">
          {bookingLanes.map(({ booking, laneIndex, followingBooking }) => (
            <BookingCard
              key={booking?.bookingPublicId ?? `${booking?.startDateTimeUtc}-${booking?.endDateTimeUtc}-${booking?.label}`}
              booking={booking}
              viewStartDate={viewStartDate}
              viewEndDate={viewEndDate}
              slotWidth={slotWidth}
              slotMinutes={slotMinutes}
              laneIndex={laneIndex}
              laneHeight={laneHeight}
              rowPadding={TIMELINE_ROW_PADDING}
              compact={false}
              connector={connector}
              onClick={onBookingClick}
              followingBooking={followingBooking}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const SiteCard = ({
  site,
  expanded,
  onToggle,
  onCreateBooking,
  viewStartDate,
  viewEndDate,
  onBookingClick,
  onTermsClick,
  getTermsLabel,
  isTermsDisabled,
}) => {
  const { t } = useTranslation();
  const connectors = Array.isArray(site?.connectors) ? site.connectors : [];
  const bookingCount = connectors.reduce(
    (sum, connector) => sum + countTimelineBookings(connector?.bookings),
    0
  );
  const [connectorColWidth, setConnectorColWidth] = useState(CONNECTOR_COL_WIDTH_DESKTOP);

  useEffect(() => {
    const updateConnectorWidth = () => {
      setConnectorColWidth(
        window.innerWidth < 640 ? CONNECTOR_COL_WIDTH_MOBILE : CONNECTOR_COL_WIDTH_DESKTOP
      );
    };

    updateConnectorWidth();
    window.addEventListener("resize", updateConnectorWidth);

    return () => {
      window.removeEventListener("resize", updateConnectorWidth);
    };
  }, []);

  const rawTotalMinutes = Math.max(differenceInMinutes(viewEndDate, viewStartDate), SLOT_MINUTES_QUARTER);
  const timelineScale = useMemo(() => getTimelineScale(rawTotalMinutes), [rawTotalMinutes]);
  const slotMinutes = timelineScale.slotMinutes;
  const alignedViewStart = useMemo(
    () => timelineScale.alignStart(viewStartDate),
    [timelineScale, viewStartDate]
  );
  const alignedViewEnd = useMemo(() => timelineScale.alignEnd(viewEndDate), [timelineScale, viewEndDate]);
  const totalMinutes = Math.max(differenceInMinutes(alignedViewEnd, alignedViewStart), slotMinutes);

  const slots = useMemo(
    () => buildTimelineSlots(alignedViewStart, alignedViewEnd, slotMinutes, totalMinutes),
    [alignedViewEnd, alignedViewStart, slotMinutes, totalMinutes]
  );

  const slotWidth = useMemo(() => getSlotWidth(slots.length, slotMinutes), [slots.length, slotMinutes]);
  const slotLabels = useMemo(() => slots.map((slot) => format(slot, timelineScale.labelFormat)), [slots, timelineScale.labelFormat]);
  const timelineScrollRef = useRef(null);

  useLayoutEffect(() => {
    if (!expanded || slotMinutes >= SLOT_MINUTES_DAY) {
      return;
    }

    const container = timelineScrollRef.current;
    if (!container) {
      return;
    }

    const target = roundToNearestHalfHour(new Date());
    target.setHours(target.getHours() - 1);
    if (target > alignedViewEnd) {
      return;
    }

    const minutesFromStart = Math.max(differenceInMinutes(target, alignedViewStart), 0);
    const scrollLeft = (minutesFromStart / slotMinutes) * slotWidth;
    const maxScroll = Math.max(container.scrollWidth - container.clientWidth, 0);
    container.scrollLeft = Math.min(Math.max(scrollLeft, 0), maxScroll);
  }, [expanded, alignedViewStart, alignedViewEnd, slotMinutes, slotWidth]);

  return (
    <div className="rounded-2xl border border-mute-200 bg-white shadow-lg overflow-hidden">
      <div
        className="flex cursor-pointer items-start justify-between gap-3 px-4 py-3 sm:px-5 text-left bg-gradient-to-r from-white to-slate-50"
        onClick={onToggle}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onToggle();
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        aria-label={expanded ? t("booking.collapseSite", { name: site?.siteName ?? t("entity.site") }) : t("booking.expandSite", { name: site?.siteName ?? t("entity.site") })}
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-2 sm:gap-3 flex-wrap">
            <h3 className="text-base sm:text-lg font-bold leading-tight">{site?.siteName}</h3>
          </div>
          <div className="mt-2 flex flex-row flex-wrap items-center gap-2 sm:gap-3">
            <span className="rounded-full bg-primary-100 text-primary-700 px-2 py-0.5 text-xs font-semibold">
              {t("booking.connectorsCount", { count: connectors.length })}
            </span>
            <span className="rounded-full bg-mute-100 text-mute-700 px-2 py-0.5 text-xs font-semibold">
              {t("booking.bookingsCount", { count: bookingCount })}
            </span>
            {onCreateBooking && (
              <button
                type="button"
                className="btn-xs btn-primary"
                onClick={(event) => {
                  event.stopPropagation();
                  onCreateBooking(site);
                }}
                aria-label={t("booking.createForSite", { name: site?.siteName ?? t("entity.site") })}
                title={t("booking.newBooking")}
              >
                <FontAwesomeIcon icon={faPlus} className="text-xs" />
              </button>
            )}
          </div>
        </div>

        <span className="shrink-0 text-mute-600" aria-hidden="true">
          <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} />
        </span>
      </div>

      {expanded && (
        <div className="overflow-visible">
          <div ref={timelineScrollRef} className="overflow-x-auto overflow-y-visible">
            <div style={{ minWidth: `${connectorColWidth + slots.length * slotWidth}px` }}>
              <div className="grid border-t border-mute-200 bg-mute-100" style={{ gridTemplateColumns: `${connectorColWidth}px auto` }}>
                <div className="sticky left-0 z-10 bg-mute-100 px-3 sm:px-4 py-2 sm:py-3 text-xs font-semibold uppercase tracking-wide text-mute-500 border-r border-mute-200">
                  {t("entity.connector")}
                </div>
                <div className="grid bg-mute-100" style={{ gridTemplateColumns: `repeat(${slots.length}, ${slotWidth}px)` }}>
                  {slotLabels.map((label, index) => (
                    <div
                      key={`${site?.sitePublicId}-header-${label}-${index}`}
                      className="border-r border-mute-200 px-0.5 py-2 sm:py-3 text-center text-[10px] sm:text-[11px] font-semibold tabular-nums text-mute-600 bg-mute-100"
                    >
                      {label}
                    </div>
                  ))}
                </div>
              </div>

                {connectors.length > 0 ? (
                  connectors.map((connector) => (
                    <ConnectorRow
                      key={connector?.connectorPublicId ?? connector?.chargingConnectorPublicId}
                      connector={connector}
                      viewStartDate={alignedViewStart}
                      viewEndDate={alignedViewEnd}
                      slots={slots}
                      connectorColWidth={connectorColWidth}
                      slotWidth={slotWidth}
                      slotMinutes={slotMinutes}
                      onBookingClick={(booking, connector) => onBookingClick?.({ site, connector, booking })}
                      onTermsClick={onTermsClick ? (connector) => onTermsClick?.({ site, connector }) : null}
                      termsLabel={getTermsLabel ? getTermsLabel({ site, connector }) : undefined}
                      termsDisabled={isTermsDisabled ? isTermsDisabled({ site, connector }) : false}
                    />
                  ))
                ) : (
                  <div className="px-5 py-8 text-sm text-mute-500">
                    {t("booking.noConnectors")}
                  </div>
                )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SiteCard;
